import type { GetCurrencyConversionByCodeQuery } from '../Card.graphql.generated';
import { useGetCurrencyConversionByCodeQuery } from '../Card.graphql.generated';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { isPrintSupportedCurrency } from '@shared/utils/currency/supportedCurrencies';

/**
 * Base currency: USD
 */
export type CurrencyRateData = Readonly<{
  /** Exchange rate from USD to this currency */
  exchangeRate: number;
  /** Price increase multiplier (e.g., 1.10 = 10% increase, 1.20 = 20% increase) */
  taxesAndFeesMultiplier: number;
  /** Fixed cost per card in minor units */
  fixedCostPerCardInMinorUnits: number;
}>;

export const useCurrencyConversionRate = (currency: string = ''): { data: GetCurrencyConversionByCodeQuery['currencyConversionByCode'] | undefined; loading: boolean } => {
  const { data, loading } = useGetCurrencyConversionByCodeQuery({
    variables: { currencyCode: currency },
    batchMode: 'fast',
    // Skip if currency is not set or is the default currency.
    // There's no point in fetching the currency conversion rate for the default currency.
    //
    // Also skip anything print cannot be sold in: the backend only prices the
    // PRINT_SUPPORTED_CURRENCIES set, and asking it for e.g. ZAR is the request that
    // produced the blank-price incident. Callers normalize via `usePrintCurrency`, so this
    // is a backstop for any caller that passes a raw currency through.
    skip: !currency || currency === DEFAULT_CURRENCY || !isPrintSupportedCurrency(currency),
  });

  return { data: data?.currencyConversionByCode, loading };
};
